import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { BellRing, MessageCircleQuestion } from 'lucide-react';
import { useContext, useId, useState, type FormEvent } from 'react';

import { ApiError } from '@/api/client';
import {
  hasDeviceOptIn,
  pushPermission,
  setDeviceOptIn,
  subscribeBrowser,
  useStorefrontPush,
} from '@/features/storefront/pwa/push';
import type { ProductQuestions, StorefrontQuestion } from '@/features/storefront/api/storefront';
import { CustomerAuthContext } from '@/features/storefront/account/customerAuthContext';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { useStorefrontFeatures } from '@/features/storefront/features';
import { usePartsEnvironment, useProductParts } from '@/features/storefront/parts/context';
import { EMAIL_SHAPE } from '@/features/storefront/parts/notifyMe';
import type { ProductPartProps } from '@/features/storefront/parts/ProductParts';
import { partIsWide, partOwnsSpacing } from '@/features/storefront/parts/style';
import { partFlag, partNumber, type PagePart } from '@/features/storefront/sections/parts';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';

/**
 * The product page's two conversations with a shopper: the questions other
 * people asked (and the "Ask a question" form), and the box a shopper leaves
 * their email in when the option they want has sold out.
 *
 * Both talk to the server through the data source, and both draw nothing when
 * the source has no answer to give - a plan without reviews has no questions,
 * a theme preview without the methods has neither. In the customiser
 * (`includeHidden`) neither asks anything: the questions are stand-ins and the
 * forms are inert, so a merchant who adds one sees what it will look like.
 */

const FIELD_STYLE = {
  borderColor: 'var(--storefront-muted)',
  borderRadius: 'var(--storefront-radius)',
  backgroundColor: 'var(--storefront-background)',
  color: 'var(--storefront-text)',
};
const MUTED = { color: 'var(--storefront-muted)' };

/** The margin a part had before the merchant chose one. See `parts/style.ts`. */
function ownClass(part: PagePart, fallback: string): string | undefined {
  return partOwnsSpacing(part) ? fallback : undefined;
}

function useInEditor(): boolean {
  return usePartsEnvironment()?.includeHidden ?? false;
}

/** What the customiser shows in place of a shop's real questions. */
const STAND_INS: StorefrontQuestion[] = [
  {
    id: 'stand-in-1',
    question: 'Does this run true to size?',
    answer: 'Yes - most customers take their usual size.',
    asker_name: 'Priya S.',
    answered_at: '2026-01-12T10:00:00Z',
    created_at: '2026-01-11T10:00:00Z',
  },
  {
    id: 'stand-in-2',
    question: 'Is gift wrapping available?',
    answer: null,
    asker_name: 'Rahul M.',
    answered_at: null,
    created_at: '2026-01-14T10:00:00Z',
  },
];

// --- Questions and answers ----------------------------------------------------

export function ProductQaPart({ part }: ProductPartProps) {
  const product = useProductParts();
  const editor = useInEditor();
  const source = useStorefrontData();
  if (!product) return null;
  if (editor) return <QaBody part={part} slug={product.detail.slug} items={STAND_INS} total={2} inert />;
  if (!source.productQuestions) return null;
  return <LiveQa part={part} slug={product.detail.slug} />;
}

function LiveQa({ part, slug }: { part: PagePart; slug: string }) {
  const text = useShopContent();
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const pageSize = partNumber(part, 'page_size') || 5;

  const questions = useInfiniteQuery({
    queryKey: [...scope, 'product-questions', slug, pageSize],
    queryFn: ({ pageParam }) =>
      source.productQuestions!(slug, { page: pageParam, pageSize }) as Promise<ProductQuestions>,
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.page_size < last.total ? last.page + 1 : undefined),
    // A 404 is the plan saying "no questions here", which asking again will not change.
    retry: false,
  });

  if (questions.isPending) return null;
  if (questions.isError) {
    if (questions.error instanceof ApiError && questions.error.status === 404) return null;
    return (
      <section className={cn(ownClass(part, 'mt-10'), 'w-full')}>
        <p className="text-sm" role="alert" style={MUTED}>
          {text('product_qa.load_error')}
        </p>
      </section>
    );
  }

  const items = questions.data.pages.flatMap((page) => page.items);
  return (
    <QaBody
      part={part}
      slug={slug}
      items={items}
      total={questions.data.pages[0]?.total ?? items.length}
      onMore={questions.hasNextPage ? () => void questions.fetchNextPage() : undefined}
      loadingMore={questions.isFetchingNextPage}
    />
  );
}

function QaBody({
  part,
  slug,
  items,
  total,
  inert = false,
  onMore,
  loadingMore = false,
}: {
  part: PagePart;
  slug: string;
  items: StorefrontQuestion[];
  total: number;
  inert?: boolean;
  onMore?: () => void;
  loadingMore?: boolean;
}) {
  const text = useShopContent();
  const allowQuestions = partFlag(part, 'allow_questions');
  const behindButton = partFlag(part, 'show_ask_button');
  const [open, setOpen] = useState(false);
  const heading = part.label.trim() || text('product_qa.heading');

  return (
    <section className={cn(ownClass(part, 'mt-10'), 'w-full')} aria-label={heading} data-total={total}>
      <h2 className="mb-4 flex items-center gap-2 text-xl font-bold" style={STOREFRONT_HEADING_STYLE}>
        <MessageCircleQuestion className="size-5 shrink-0" aria-hidden />
        {heading}
      </h2>

      {items.length === 0 ? (
        <p className="text-sm" style={MUTED}>
          {text('product_qa.empty')}
        </p>
      ) : (
        <ul className="divide-y" style={{ borderColor: 'var(--storefront-surface)' }}>
          {items.map((item) => (
            <li key={item.id} className="py-4 text-sm">
              <p className="font-semibold">
                <span aria-hidden>Q. </span>
                {item.question}
              </p>
              <p className="mt-0.5 text-xs" style={MUTED}>
                {text('product_qa.asked_by', { name: item.asker_name })}
              </p>
              {item.answer ? (
                <div className="mt-2 border-s-2 ps-3" style={{ borderColor: 'var(--storefront-primary)' }}>
                  <p className="text-xs font-semibold" style={{ color: 'var(--storefront-primary)' }}>
                    {text('product_qa.answer_from')}
                  </p>
                  <p className="mt-0.5 whitespace-pre-line">{item.answer}</p>
                </div>
              ) : (
                <p className="mt-2 text-xs italic" style={MUTED}>
                  {text('product_qa.awaiting')}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {onMore ? (
        <button
          type="button"
          onClick={onMore}
          disabled={loadingMore}
          className="mt-2 text-sm font-semibold underline underline-offset-2 disabled:opacity-50"
          style={{ color: 'var(--storefront-primary)' }}
        >
          {text('product_qa.more')}
        </button>
      ) : null}

      {allowQuestions ? (
        behindButton && !open ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="mt-4 inline-flex h-11 items-center px-6 text-sm font-semibold"
            style={STOREFRONT_BUTTON_STYLE}
          >
            {text('product_qa.ask_button')}
          </button>
        ) : (
          <AskForm slug={slug} inert={inert} onCancel={behindButton ? () => setOpen(false) : undefined} />
        )
      ) : null}
    </section>
  );
}

function AskForm({ slug, inert, onCancel }: { slug: string; inert: boolean; onCancel?: () => void }) {
  const text = useShopContent();
  const source = useStorefrontData();
  const auth = useContext(CustomerAuthContext);
  const signedIn = auth?.status === 'authenticated' && Boolean(auth.customer);
  const ids = useId();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [question, setQuestion] = useState('');
  const [website, setWebsite] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const ask = useMutation({
    mutationFn: () =>
      source.askProductQuestion!(slug, {
        question: question.trim(),
        ...(signedIn ? {} : { name: name.trim() }),
        ...(!signedIn && email.trim() ? { email: email.trim() } : {}),
        ...(website ? { website } : {}),
      }),
    onSuccess: () => {
      setSent(true);
      setQuestion('');
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (inert || !source.askProductQuestion) return;
    if (!signedIn && !name.trim()) {
      setProblem(text('product_qa.name_required'));
      return;
    }
    if (!question.trim()) {
      setProblem(text('product_qa.question_required'));
      return;
    }
    if (!signedIn && email.trim() && !EMAIL_SHAPE.test(email.trim())) {
      setProblem(text('notify_me.invalid_email'));
      return;
    }
    setProblem(null);
    ask.mutate();
  };

  if (sent) {
    return (
      <p role="status" className="mt-4 text-sm font-semibold" style={{ color: 'var(--storefront-primary)' }}>
        {text('product_qa.sent')}
      </p>
    );
  }

  return (
    <form className="mt-4 space-y-3" onSubmit={submit} noValidate>
      {!signedIn ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm" htmlFor={`${ids}-name`}>
            <span className="mb-1 block font-medium">{text('product_qa.name')}</span>
            <input
              id={`${ids}-name`}
              value={name}
              maxLength={120}
              autoComplete="name"
              onChange={(event) => setName(event.target.value)}
              className="h-10 w-full border px-3"
              style={FIELD_STYLE}
            />
          </label>
          <label className="block text-sm" htmlFor={`${ids}-email`}>
            <span className="mb-1 block font-medium">{text('product_qa.email')}</span>
            <input
              id={`${ids}-email`}
              type="email"
              value={email}
              maxLength={320}
              autoComplete="email"
              onChange={(event) => setEmail(event.target.value)}
              className="h-10 w-full border px-3"
              style={FIELD_STYLE}
            />
          </label>
        </div>
      ) : null}
      <label className="block text-sm" htmlFor={`${ids}-question`}>
        <span className="mb-1 block font-medium">{text('product_qa.question')}</span>
        <textarea
          id={`${ids}-question`}
          value={question}
          maxLength={500}
          rows={3}
          placeholder={text('product_qa.question_placeholder')}
          onChange={(event) => setQuestion(event.target.value)}
          className="w-full border px-3 py-2"
          style={FIELD_STYLE}
        />
      </label>
      {/* The honeypot: off-screen and out of the tab order, so only a script fills it. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        value={website}
        onChange={(event) => setWebsite(event.target.value)}
        className="absolute -left-[9999px] h-px w-px opacity-0"
      />
      {problem ? (
        <p role="alert" className="text-sm text-red-600">
          {problem}
        </p>
      ) : ask.isError ? (
        <p role="alert" className="text-sm text-red-600">
          {text('product_qa.error')}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={ask.isPending}
          className="inline-flex h-11 items-center px-6 text-sm font-semibold disabled:opacity-50"
          style={STOREFRONT_BUTTON_STYLE}
        >
          {text('product_qa.submit')}
        </button>
        {onCancel ? (
          <button type="button" onClick={onCancel} className="text-sm underline underline-offset-2">
            {text('product_qa.cancel')}
          </button>
        ) : null}
      </div>
    </form>
  );
}

// --- Notify me when available -------------------------------------------------

export function ProductNotifyMePart({ part, presentation }: ProductPartProps) {
  const product = useProductParts();
  const editor = useInEditor();
  const source = useStorefrontData();
  const { cart: sellsThings } = useStorefrontFeatures();
  if (!product || !sellsThings) return null;
  const selected = product.selected;
  // Only while the chosen option is sold out; the customiser always shows it,
  // or a merchant whose shop is fully stocked could never see what they added.
  if (!editor && (!selected || selected.in_stock || !source.subscribeStockAlert)) return null;
  return (
    <NotifyForm
      // A fresh form per option: "Done" for the red one says nothing about the blue.
      key={selected?.id ?? 'none'}
      part={part}
      variantId={selected?.id ?? ''}
      inert={editor}
      wide={partIsWide(part, presentation)}
    />
  );
}

function NotifyForm({
  part,
  variantId,
  inert,
  wide,
}: {
  part: PagePart;
  variantId: string;
  inert: boolean;
  wide: boolean;
}) {
  const text = useShopContent();
  const source = useStorefrontData();
  const auth = useContext(CustomerAuthContext);
  const inputId = useId();
  const [email, setEmail] = useState(auth?.customer?.email ?? '');
  const [invalid, setInvalid] = useState(false);
  // "Also tell this device", where the shop offers push and the browser has
  // not already said no. Ticked for a device that said yes before.
  const push = useStorefrontPush();
  const pushOffered = Boolean(push) && !inert && ['default', 'granted'].includes(pushPermission());
  const [pushToo, setPushToo] = useState(() => (push ? hasDeviceOptIn(push) : false));

  const subscribe = useMutation({
    mutationFn: async (address: string) => {
      let subscription = null;
      if (push && pushOffered && pushToo) {
        // Asked from the shopper's own tap on the button; a no, or a browser
        // that cannot, still subscribes the email.
        subscription = await subscribeBrowser(push).catch(() => null);
        if (subscription) setDeviceOptIn(push, true);
      }
      await (subscription
        ? source.subscribeStockAlert!(variantId, address, subscription)
        : source.subscribeStockAlert!(variantId, address));
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (inert) return;
    const address = email.trim();
    if (!EMAIL_SHAPE.test(address)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    subscribe.mutate(address);
  };

  return (
    <div
      className={cn(ownClass(part, 'mt-8'), 'border p-4 text-sm', wide ? 'w-full' : 'w-full sm:max-w-md')}
      style={{ borderColor: 'var(--storefront-surface)', borderRadius: 'var(--storefront-radius)' }}
      data-part="notify-me"
    >
      <p className="flex items-center gap-2 font-semibold">
        <BellRing className="size-4 shrink-0" aria-hidden />
        {text('notify_me.heading')}
      </p>
      {subscribe.isSuccess ? (
        <p role="status" className="mt-2 font-semibold" style={{ color: 'var(--storefront-primary)' }}>
          {text('notify_me.done')}
        </p>
      ) : (
        <>
          <label htmlFor={inputId} className="mt-1 block" style={MUTED}>
            {text('notify_me.prompt')}
          </label>
          <form className="mt-3 flex gap-2" onSubmit={submit} noValidate>
            <input
              id={inputId}
              type="email"
              autoComplete="email"
              value={email}
              maxLength={320}
              placeholder={text('notify_me.email_placeholder')}
              aria-invalid={invalid || undefined}
              onChange={(event) => setEmail(event.target.value)}
              className="h-10 min-w-0 flex-1 border bg-transparent px-3 text-sm"
              style={{ borderColor: 'var(--storefront-surface)', borderRadius: 'var(--storefront-radius)' }}
            />
            <button
              type="submit"
              disabled={subscribe.isPending}
              className="h-10 shrink-0 px-4 text-sm font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: 'var(--storefront-primary)', borderRadius: 'var(--storefront-radius)' }}
            >
              {part.label.trim() || text('notify_me.submit')}
            </button>
          </form>
          {pushOffered ? (
            <label className="mt-2 flex items-center gap-2" style={MUTED}>
              <input
                type="checkbox"
                checked={pushToo}
                onChange={(event) => setPushToo(event.target.checked)}
              />
              {text('notify_me.push_too')}
            </label>
          ) : null}
          {invalid ? (
            <p role="alert" className="mt-2 text-red-600">
              {text('notify_me.invalid_email')}
            </p>
          ) : subscribe.isError ? (
            <p role="alert" className="mt-2 text-red-600">
              {text('notify_me.error')}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
